import { Select } from "./Select";
import {
  useMemo,
  useState,
  useEffect,
  useRef,
  useId,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { MapEditor } from "./MapEditor";
import { QrField } from "./QrField";
import { DynamicSourceParameters } from "./DynamicSourceParameters";
import { DamPicker } from "./DamPicker";
import { ImageChoices } from "./ImageChoices";
import { StatementPicker } from "./StatementPicker";
import { AssetImage } from "./AssetImage";
import { AssetLibraryPicker } from "./AssetLibraryPicker";
import { AdjustedImage, ImageAdjustmentDialog } from "./ImageAdjustmentDialog";
import {
  defaultBackgroundRemover,
  type BackgroundRemovalService,
} from "./background-remover";
import {
  defaultAssetUrl,
  imageNeedsTransform,
  type AssetLoader,
  type ImageTransformer,
} from "./images";
import type { MapFormServices, DamFormServices } from "@proof/core";
import {
  resolve,
  formVisibleFieldIds,
  isDynamicChoiceField,
  validateSubmission,
  applyPreset,
  displayValue,
  richTextRuns,
  defaultAdjustments,
  damSelectionSchema,
  backgroundRemoval,
  type Project,
  type Values,
  type Field,
  type Adjustments,
  type Context,
  type Choice,
} from "@proof/core";
export interface AssetInfo {
  id: string;
  name: string;
  width: number;
  height: number;
  bytes: number;
}
export interface FormGeneratorProps {
  removeBackground?: BackgroundRemovalService;
  sourceParameters?: import("@proof/core").SourceParameters;
  onSourceParametersChange?: (
    values: import("@proof/core").SourceParameters,
    forceRefresh: boolean,
  ) => void;
  dam?: DamFormServices;
  loadAsset?: AssetLoader;
  transformImage?: ImageTransformer;
  maps?: MapFormServices;
  project: Project;
  values: Values;
  partId?: string;
  context?: Context;
  onChange: (values: Values) => void;
  upload?: (
    file: File,
    field: Field,
    signal?: AbortSignal,
  ) => Promise<AssetInfo>;
  assets?: AssetInfo[];
  /** Per-field asset lists (host source inputs) that replace `assets`. */
  fieldAssets?: Record<string, AssetInfo[]>;
  assetUrl?: (id: string) => string;
  showValidation?: boolean;
  compact?: boolean;
  /** Content above the sections, such as a single profile switcher. */
  header?: ReactNode;
  /** Content at the top of a section, such as that section's profile switcher. */
  sectionHeader?: (groupId: string) => ReactNode;
}
export function FormGenerator({
  header,
  sectionHeader,
  removeBackground = defaultBackgroundRemover,
  sourceParameters,
  onSourceParametersChange,
  dam,
  maps,
  project,
  values,
  partId,
  context,
  onChange,
  upload,
  loadAsset,
  transformImage,
  assets = [],
  fieldAssets,
  assetUrl = defaultAssetUrl,
  showValidation = false,
  compact = false,
}: FormGeneratorProps) {
  const r = useMemo(
      () => resolve(project, values, partId, context),
      [project, values, partId, context],
    ),
    validation = useMemo(
      () =>
        showValidation
          ? validateSubmission(project, values, partId, context).errors
          : [],
      [project, values, partId, context, showValidation],
    );
  const [tab, setTab] = useState("all");
  const requestScope = useMemo(
    () => JSON.stringify([project, partId, context, values]),
    [project, partId, context, values],
  );
  const update = (f: Field, value: Values[string]) =>
    onChange(
      f.type === "preset"
        ? applyPreset(project, values, f.id, String(value), partId, context)
        : { ...values, [f.id]: value },
    );
  const tabs = [...project.tabs].sort((a, b) => a.order - b.order);
  const mappedFields = formVisibleFieldIds(project);
  const groupIds =
    tab === "all" ? undefined : tabs.find((t) => t.id === tab)?.groupIds;
  const groups = project.groups
    .filter(
      (g) =>
        r.groups.has(g.id) &&
        (!groupIds || groupIds.includes(g.id)) &&
        (!mappedFields ||
          r.fields.some((f) => f.groupId === g.id && mappedFields.has(f.id))),
    )
    .sort((a, b) => a.order - b.order);
  // Only the columns the visible sections use are laid out, split evenly:
  // column 2 alone fills the form; columns 2 and 3 share it 50/50.
  const usedColumns = [...new Set(groups.map((g) => g.column))].sort(
    (a, b) => a - b,
  );
  const field = (f: Field) =>
    (!mappedFields || mappedFields.has(f.id)) &&
    (!f.formDisplay || f.formDisplay === "show") &&
    r.visible.has(f.id) &&
    (!groupIds || !f.groupId || groupIds.includes(f.groupId)) ? (
      <FieldInput
        key={f.id}
        statementProject={project}
        field={{ ...f, choices: r.choices[f.id] }}
        values={r.values}
        options={r.choices[f.id]}
        value={r.values[f.id]}
        onChange={(value) => update(f, value)}
        assets={fieldAssets?.[f.id] ?? assets}
        upload={upload}
        loadAsset={loadAsset}
        transformImage={transformImage}
        removeBackground={removeBackground}
        maps={maps}
        dam={dam}
        requestScope={requestScope}
        assetUrl={assetUrl}
        error={validation.find((e) => e.fieldId === f.id)?.message}
      />
    ) : null;
  const packed = project.settings.formSectionLayout === "stacked" && !compact;
  const section = (g: Project["groups"][number]) => (
    <section
      key={g.id}
      className="form-group"
      style={{
        gridColumn:
          compact || packed ? undefined : usedColumns.indexOf(g.column) + 1,
      }}
    >
      <h3>{g.name}</h3>
      {g.note && <p className="muted">{g.note}</p>}
      {sectionHeader?.(g.id)}
      {r.fields.filter((f) => f.groupId === g.id).map(field)}
    </section>
  );
  return (
    <div className={`proof-form ${compact ? "compact" : ""}`}>
      {onSourceParametersChange && (
        <DynamicSourceParameters
          project={project}
          partId={partId}
          values={sourceParameters}
          onChange={onSourceParametersChange}
        />
      )}
      {header}
      {tabs.length > 1 && (
        <div className="form-tabs" role="tablist" aria-label="Form sections">
          <button
            role="tab"
            aria-selected={tab === "all"}
            onClick={() => setTab("all")}
          >
            All fields
          </button>
          {tabs.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
            >
              {t.name}
            </button>
          ))}
        </div>
      )}
      <div
        className="form-groups"
        style={{
          gridTemplateColumns: compact
            ? undefined
            : `repeat(${Math.max(1, usedColumns.length)}, ${
                project.settings.formColumnWidth
                  ? `minmax(0, ${project.settings.formColumnWidth}px)`
                  : "minmax(0, 1fr)"
              })`,
          justifyContent:
            !compact && project.settings.formColumnWidth
              ? project.settings.formAlignX || "start"
              : undefined,
          justifyItems:
            project.settings.formAlignX &&
            !project.settings.formColumnWidth
              ? project.settings.formAlignX
              : undefined,
          alignItems: project.settings.formAlignY || undefined,
        }}
        data-align-x={project.settings.formAlignX || undefined}
        data-align-y={project.settings.formAlignY || undefined}
      >
        {packed
          ? usedColumns.map((column) => (
              <div className="form-section-column" key={column}>
                {groups.filter((g) => g.column === column).map(section)}
              </div>
            ))
          : groups.map(section)}
        {(project.groups.length === 0 ||
          (project.formInstances.some(
            (i) => !i.profileOnly && i.mappedOnly !== undefined,
          ) &&
            r.fields.some(
              (f) => !f.groupId && (!mappedFields || mappedFields.has(f.id)),
            ))) && (
          <section className="form-group ungrouped">
            {r.fields.filter((f) => !f.groupId).map(field)}
          </section>
        )}
      </div>
      {r.price > 0 && (
        <div className="form-upcharge">
          Personalization options +
          {new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(r.price)}
        </div>
      )}
    </div>
  );
}
export interface FieldInputProps {
  removeBackground?: BackgroundRemovalService;
  statementProject?: Project;
  dam?: DamFormServices;
  loadAsset?: AssetLoader;
  transformImage?: ImageTransformer;
  maps?: MapFormServices;
  requestScope?: string;
  options?: Choice[];
  field: Field;
  values?: Values;
  value: Values[string];
  onChange: (value: Values[string]) => void;
  upload?: FormGeneratorProps["upload"];
  assets?: AssetInfo[];
  assetUrl?: (id: string) => string;
  error?: string;
}
export function FieldInput({
  removeBackground = defaultBackgroundRemover,
  statementProject,
  dam,
  maps,
  requestScope,
  options,
  field: f,
  values = {},
  value,
  onChange,
  upload,
  loadAsset,
  transformImage,
  assets = [],
  assetUrl = defaultAssetUrl,
  error,
}: FieldInputProps) {
  const instanceId = useId();
  const id = `input_${instanceId}`,
    help = `help_${instanceId}`;
  const [busy, setBusy] = useState(false),
    [uploadError, setUploadError] = useState(""),
    [controller, setController] = useState<AbortController | null>(null);
  const disabled = !f.editable || f.system;
  const scope = requestScope || JSON.stringify([f, value]),
    liveScope = useRef(scope),
    pendingUpload = useRef<AbortController | undefined>(undefined);
  liveScope.current = scope;
  const authority = useMemo(
    () => ({}),
    [scope, upload, loadAsset, transformImage, assetUrl, removeBackground],
  );
  const liveAuthority = useRef(authority);
  liveAuthority.current = authority;
  const [imageEdit, setImageEdit] = useState<{
    scope: string;
    authority: object;
    value: {
      assetId: string;
      adjustments?: Adjustments;
      [key: string]: unknown;
    };
    size?: { width: number; height: number };
  }>();
  useEffect(() => {
    pendingUpload.current?.abort();
    pendingUpload.current = undefined;
    setBusy(false);
    setController(null);
    setUploadError("");
    setImageEdit(undefined);
    return () => {
      pendingUpload.current?.abort();
      pendingUpload.current = undefined;
    };
  }, [authority]);
  const common = {
    id,
    disabled,
    required: f.required,
    "aria-invalid": !!error,
    "aria-describedby": help,
  };
  const s =
    f.calculation && f.type === "richtext"
      ? richTextRuns(displayValue(value))
          .map((run) => run.text)
          .join("")
      : displayValue(value);
  const selectedImage =
    f.type === "image-list" ? f.choices.find((c) => c.value === s) : undefined;
  const selectOptions =
    options ||
    (f.type === "preset"
      ? f.presets.map((p) => ({ value: p.id, label: p.label }))
      : f.choices);
  let input;
  const thumbnails =
    f.type === "image-list" &&
    f.source.kind !== "dam" &&
    f.choicePresentation === "thumbnails";
  const choiceGroup =
    f.type === "radio" ||
    (f.type === "checkbox" &&
      (f.choices.length > 0 || isDynamicChoiceField(f)));
  const imageControl =
    f.type === "image" ||
    f.type === "map" ||
    (f.type === "image-list" && f.source.kind === "dam");
  const groupLabel =
    choiceGroup ||
    f.type === "address" ||
    (imageControl && (disabled || !f.image.allowUpload || !upload)) ||
    f.type === "qr";
  const labelContents = (
    <>
      {f.label}
      {f.required && <span className="required-mark"> *</span>}
      {disabled && (
        <span className="field-tag">{f.system ? "System" : "Read only"}</span>
      )}
    </>
  );
  const change = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => onChange(e.target.value);
  if (f.type === "textarea" || f.type === "richtext")
    input = (
      <textarea
        {...common}
        rows={f.type === "richtext" ? 5 : 3}
        value={s}
        onChange={change}
        maxLength={f.validation.maxLength}
        placeholder={
          f.type === "richtext"
            ? "Formatted text (HTML paragraphs, strong, em)…"
            : undefined
        }
      />
    );
  else if (f.type === "color")
    input = (
      <input
        {...common}
        type="color"
        value={/^#[0-9a-f]{6}$/i.test(s) ? s : "#000000"}
        onChange={change}
      />
    );
  else if (thumbnails)
    input = (
      <ImageChoices
        id={id}
        label={f.label}
        options={selectOptions}
        value={s}
        required={f.required}
        disabled={disabled}
        system={f.system}
        helpId={help}
        invalid={!!error}
        assetUrl={assetUrl}
        loadAsset={loadAsset}
        onChange={onChange}
      />
    );
  else if (
    ["dropdown", "image-list", "preset"].includes(f.type) &&
    f.source.kind !== "dam"
  )
    input = (
      <>
        <Select {...common} value={s} onChange={change}>
          <option value="">Choose an option</option>
          {disabled &&
            s !== "" &&
            !selectOptions.some((c) => c.value === s) && (
              <option value={s}>{s}</option>
            )}
          {selectOptions.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </Select>
        {selectedImage &&
          (selectedImage.assetId || selectedImage.image) &&
          (selectedImage.assetId ? (
            <AssetImage
              assetId={selectedImage.assetId}
              assetUrl={assetUrl}
              loadAsset={loadAsset}
              className="choice-thumbnail"
              alt={selectedImage.label}
            />
          ) : (
            <img
              className="choice-thumbnail"
              alt={selectedImage.label}
              src={
                selectedImage.assetId
                  ? assetUrl(selectedImage.assetId)
                  : selectedImage.image
              }
            />
          ))}
      </>
    );
  else if (f.type === "combobox")
    input = (
      <>
        <input {...common} list={`${id}_options`} value={s} onChange={change} />
        <datalist id={`${id}_options`}>
          {selectOptions.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </datalist>
      </>
    );
  else if (choiceGroup)
    input = (
      <fieldset
        id={id}
        className="choice-field"
        disabled={disabled}
        aria-describedby={help}
        aria-invalid={!!error}
      >
        <legend className="sr-only">{f.label}</legend>
        {!f.choices.length && (
          <span className="muted">No options available.</span>
        )}
        {f.choices.map((c) => (
          <label key={c.value}>
            <input
              name={id}
              type={f.type === "radio" ? "radio" : "checkbox"}
              checked={
                f.type === "radio"
                  ? s === c.value
                  : Array.isArray(value) && value.includes(c.value)
              }
              onChange={(e) =>
                onChange(
                  f.type === "radio"
                    ? c.value
                    : e.target.checked
                      ? [...(Array.isArray(value) ? value : []), c.value]
                      : (Array.isArray(value) ? value : []).filter(
                          (v) => v !== c.value,
                        ),
                )
              }
            />
            <span>{c.label}</span>
          </label>
        ))}
      </fieldset>
    );
  else if (f.type === "checkbox")
    input = (
      <label className="checkbox-field">
        <input
          {...common}
          type="checkbox"
          checked={value === true || value === "true"}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{f.label}</span>
      </label>
    );
  else if (
    f.type === "image" ||
    f.type === "map" ||
    (f.type === "image-list" && f.source.kind === "dam")
  ) {
    const image =
      typeof value === "string"
        ? { assetId: value, adjustments: defaultAdjustments() }
        : value && typeof value === "object" && !Array.isArray(value)
          ? (value as { assetId: string; adjustments?: Adjustments })
          : null;
    const background = backgroundRemoval(value);
    const a = {
      ...defaultAdjustments(),
      ...image?.adjustments,
      ...(background ? { backgroundMaskAssetId: background.assetId } : {}),
    };
    const handleFile = async (file: File) => {
      if (
        !upload ||
        disabled ||
        !f.image.allowUpload ||
        busy ||
        pendingUpload.current
      )
        return;
      setUploadError("");
      if (file.size > f.image.maxBytes) {
        setUploadError("Image exceeds the configured file size limit.");
        return;
      }
      const abort = new AbortController();
      const startedScope = scope;
      const startedAuthority = authority;
      pendingUpload.current = abort;
      setController(abort);
      setBusy(true);
      try {
        const asset = await upload(file, f, abort.signal);
        if (
          !abort.signal.aborted &&
          liveScope.current === startedScope &&
          liveAuthority.current === startedAuthority
        )
          setImageEdit({
            scope: startedScope,
            authority: startedAuthority,
            value: { assetId: asset.id, adjustments: defaultAdjustments() },
            size: asset,
          });
      } catch (e) {
        if (
          !abort.signal.aborted &&
          liveScope.current === startedScope &&
          liveAuthority.current === startedAuthority
        )
          setUploadError((e as Error).message);
      } finally {
        if (pendingUpload.current === abort) {
          pendingUpload.current = undefined;
          setBusy(false);
          setController(null);
        }
      }
    };
    input = (
      <div
        className="image-field"
        role="group"
        aria-label={f.label}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          if (disabled || busy || !f.image.allowUpload || !upload) return;
          if (event.dataTransfer.files.length !== 1)
            setUploadError("Choose exactly one image.");
          else void handleFile(event.dataTransfer.files[0]);
        }}
      >
        {f.source.kind === "dam" && (
          <DamPicker
            field={f}
            services={dam}
            disabled={disabled}
            scope={scope}
            onChange={(next) => {
              onChange(next);
              if (
                !disabled &&
                next &&
                typeof next === "object" &&
                !Array.isArray(next) &&
                typeof (next as { assetId?: unknown }).assetId === "string"
              )
                setImageEdit({
                  scope,
                  authority,
                  value: {
                    ...(next as Record<string, unknown>),
                    assetId: (next as { assetId: string }).assetId,
                    adjustments: {
                      ...defaultAdjustments(),
                      ...((next as { adjustments?: Adjustments }).adjustments ||
                        {}),
                    },
                  },
                });
            }}
          />
        )}
        {f.type === "map" && maps && (
          <MapEditor
            key={`${statementProject?.tenantId}:${statementProject?.id}:${f.id}`}
            values={values}
            field={f}
            services={maps}
            assetUrl={assetUrl}
            loadAsset={loadAsset}
            disabled={disabled}
            scope={requestScope || JSON.stringify([f, value])}
            onChange={onChange}
          />
        )}
        {f.type === "map" &&
          image &&
          typeof value === "object" &&
          value &&
          "attribution" in value && (
            <p className="field-help">{String(value.attribution)}</p>
          )}
        {image?.assetId && (
          <AdjustedImage
            assetId={image.assetId}
            assetUrl={assetUrl}
            loadAsset={loadAsset}
            transformImage={transformImage}
            adjustments={a}
            className="asset-thumbnail"
            alt={`${f.label} selected image`}
          />
        )}
        {f.source.kind === "dam" &&
          value &&
          typeof value === "object" &&
          !Array.isArray(value) &&
          Object.hasOwn(value, "dam") && (
            <button
              type="button"
              onClick={() => {
                const parsed = damSelectionSchema.safeParse(value);
                if (!parsed.success) return;
                const url = URL.createObjectURL(
                  new Blob(
                    [JSON.stringify({ ...parsed.data, adjustments: a })],
                    { type: "application/json" },
                  ),
                );
                const link = document.createElement("a");
                link.href = url;
                link.download = `${f.id}-image-selection.json`;
                link.click();
                setTimeout(() => URL.revokeObjectURL(url), 0);
              }}
            >
              Download image selection
            </button>
          )}
        {disabled && (
          <p className="field-help image-readonly-note">
            This image is provided by the document or connected data.
          </p>
        )}
        {!disabled && f.image.allowUpload && upload && (
          <label className="upload-zone">
            <input
              {...common}
              type="file"
              disabled={disabled || busy || !f.image.allowUpload || !upload}
              accept="image/png,image/jpeg,image/webp,image/tiff"
              onChange={(e) => {
                if (e.target.files?.[0]) void handleFile(e.target.files[0]);
                e.target.value = "";
              }}
            />
            <span>
              {busy
                ? "Uploading image…"
                : image?.assetId
                  ? "Replace image"
                  : "Drop an image or browse"}
            </span>
            <small>
              PNG, JPEG, WebP, TIFF · up to{" "}
              {Math.round(f.image.maxBytes / 1000000)} MB
            </small>
          </label>
        )}
        {busy && (
          <button
            type="button"
            onClick={() => {
              controller?.abort();
              pendingUpload.current = undefined;
              setBusy(false);
              setController(null);
            }}
          >
            Cancel upload
          </button>
        )}
        {uploadError && (
          <p role="alert" className="error-text">
            {uploadError}
          </p>
        )}
        {assets.length > 0 && f.source.kind !== "dam" && (
          <AssetLibraryPicker
            label={f.label}
            disabled={disabled}
            value={image?.assetId || ""}
            assets={assets}
            scope={scope}
            assetUrl={assetUrl}
            loadAsset={loadAsset}
            onChange={(assetId) => {
              const next = assetId
                ? { assetId, adjustments: defaultAdjustments() }
                : null;
              onChange(next);
              // A picked image goes straight to Edit image, like an upload.
              if (next && !disabled)
                setImageEdit({
                  scope,
                  authority,
                  value: next,
                  size: assets.find((asset) => asset.id === assetId),
                });
            }}
          />
        )}
        {image?.assetId && (
          <div className="image-field-actions">
            {(f.image.tools.length > 0 || imageNeedsTransform(a)) && (
              <button
                type="button"
                aria-haspopup="dialog"
                onClick={() =>
                  setImageEdit({
                    scope,
                    authority,
                    value: {
                      ...(typeof value === "object" &&
                      value &&
                      !Array.isArray(value)
                        ? value
                        : {}),
                      assetId: image.assetId,
                      adjustments: a,
                    },
                  })
                }
              >
                {disabled ? "View image" : "Edit image"}
              </button>
            )}
            {!disabled &&
              (imageNeedsTransform(a) || backgroundRemoval(value)) && (
                <button
                  type="button"
                  onClick={() => {
                    const { backgroundRemoval: _removed, ...original } =
                      typeof value === "object" &&
                      value &&
                      !Array.isArray(value)
                        ? value
                        : {};
                    onChange({
                      ...original,
                      assetId: image.assetId,
                      adjustments: defaultAdjustments(),
                    });
                  }}
                >
                  Reset edits
                </button>
              )}
            {!disabled && (
              <button type="button" onClick={() => onChange(null)}>
                Remove image
              </button>
            )}
          </div>
        )}
        {imageEdit?.scope === scope && imageEdit.authority === authority && (
          <ImageAdjustmentDialog
            key={imageEdit.scope + imageEdit.value.assetId}
            field={f}
            assetId={imageEdit.value.assetId}
            initial={{
              ...defaultAdjustments(),
              ...imageEdit.value.adjustments,
            }}
            readOnly={disabled}
            assetUrl={assetUrl}
            loadAsset={loadAsset}
            transformImage={transformImage}
            backgroundRemoval={backgroundRemoval(imageEdit.value)}
            removeBackground={removeBackground}
            upload={upload}
            size={
              imageEdit.size ||
              assets.find((asset) => asset.id === imageEdit.value.assetId)
            }
            onClose={() => setImageEdit(undefined)}
            onSave={(adjustments, background) => {
              if (
                disabled ||
                imageEdit.scope !== liveScope.current ||
                imageEdit.authority !== liveAuthority.current
              )
                return;
              setImageEdit(undefined);
              const { backgroundRemoval: _previous, ...original } =
                imageEdit.value;
              onChange({
                ...original,
                adjustments,
                ...(background ? { backgroundRemoval: background } : {}),
              });
            }}
          />
        )}
      </div>
    );
  } else if (f.type === "address") {
    const address =
      value && typeof value === "object" && !Array.isArray(value) ? value : {};
    input = (
      <div className="address-field" role="group" aria-label={f.label}>
        {["street", "city", "state", "postalCode", "country"].map((key) => (
          <input
            key={key}
            aria-label={`${f.label} ${key}`}
            placeholder={key}
            disabled={disabled}
            value={String(address[key] || "")}
            onChange={(e) => onChange({ ...address, [key]: e.target.value })}
          />
        ))}
      </div>
    );
  } else if (f.type === "qr") {
    input = (
      <QrField
        field={f}
        value={value}
        values={values}
        onChange={onChange}
        upload={upload}
        assetUrl={assetUrl}
        loadAsset={loadAsset}
        scope={scope}
        disabled={disabled}
      />
    );
  } else
    input = (
      <input
        {...common}
        className={
          f.type === "number" || f.format.kind === "number"
            ? "numeric"
            : undefined
        }
        type={
          f.type === "datetime"
            ? "datetime-local"
            : f.type === "calendar"
              ? "date"
              : ["date", "time", "email", "number"].includes(f.type)
                ? f.type
                : f.type === "phone"
                  ? "tel"
                  : "text"
        }
        value={s}
        onChange={change}
        maxLength={f.validation.maxLength}
        min={f.validation.min}
        max={f.validation.max}
        inputMode={
          f.type === "zip" ? "numeric" : f.type === "uri" ? "url" : undefined
        }
        autoComplete={f.type === "zip" ? "postal-code" : undefined}
      />
    );
  return (
    <div
      className={`form-field ${error ? "invalid" : ""}`}
      data-field-id={f.id}
    >
      {!thumbnails &&
        !(
          f.type === "checkbox" &&
          !f.choices.length &&
          !isDynamicChoiceField(f)
        ) &&
        (groupLabel ? (
          <span className="form-field-label">{labelContents}</span>
        ) : (
          <label htmlFor={id}>{labelContents}</label>
        ))}
      {f.source.kind === "statement" && (
        <StatementPicker
          project={statementProject}
          field={f}
          value={value}
          disabled={disabled}
          onChange={onChange}
        />
      )}
      {input}
      <div id={help}>
        {f.help && <small className="field-help">{f.help}</small>}
        {f.type === "richtext" && (
          <small className="field-help">
            Use &lt;p&gt;, &lt;strong&gt; and &lt;em&gt; for formatted text.
          </small>
        )}
        {error && (
          <small role="alert" className="error-text">
            {error}
          </small>
        )}
      </div>
    </div>
  );
}
